import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { ApiError } from "../api/client.js";
import type {
  DesignDecisionsResponse,
  EmitterAction,
  EmitterKind,
  RadiatorKind,
  RoomEmitterInput,
  UnderfloorZone,
} from "../api/types.js";
import { usePortal } from "../app/context.js";
import { href } from "../app/router.js";
import { humanise } from "../lib/format.js";

/**
 * Room-by-room manual emitter selection for one job.
 *
 * Same "typed product + calc verdict" pattern as HeatPumpSelection, per
 * Josh's §07 decision. The screen lists the rooms the survey found, lets the
 * designer record their emitter choice per room (radiator or underfloor;
 * retain / replace / new), and shows the calc chain's per-room warnings
 * from assessEmitters.
 *
 * The room list comes from `response.computed.emitters` (populated once a
 * survey is on the job); when there is no survey yet, the screen shows the
 * missing-items list from the design pack completeness gate instead of an
 * empty room list a designer could fill in against nothing.
 */

const EMITTER_KINDS: readonly EmitterKind[] = ["radiator", "underfloor"];
const EMITTER_ACTIONS: readonly EmitterAction[] = ["retain", "replace", "new"];
const RADIATOR_KINDS: readonly RadiatorKind[] = [
  "panel-radiator",
  "column-radiator",
  "towel-rail",
  "natural-convector",
  "fan-convector",
];
const UNDERFLOOR_ZONES: readonly UnderfloorZone[] = [
  "occupied",
  "bathroom",
  "peripheral",
];

/**
 * The room list. Comes from the computed emitters (populated whenever a
 * survey has been recorded), each of which carries a stable id equal to
 * the surveyed room's id.
 */
interface Room {
  readonly id: string;
  readonly name: string;
}

function roomsFrom(response: DesignDecisionsResponse | null): Room[] {
  const computed = response?.computed;
  if (!computed) return [];
  // Every heat-loss room (2026-09-11). The computed emitters list only rooms
  // with an emitter designed at the stored flow temperature, so a room with
  // none yet — or every room, before the flow temperature is set — would
  // otherwise have no row to record its emitter in.
  if (computed.rooms) return computed.rooms.map((r) => ({ id: r.roomId, name: r.name }));
  return computed.emitters
    .map((raw) => {
      const e = raw as { roomId?: unknown; roomName?: unknown };
      const id = typeof e.roomId === "string" ? e.roomId : "";
      const name = typeof e.roomName === "string" ? e.roomName : id;
      return { id, name };
    })
    .filter((r) => r.id !== "");
}

export function EmitterSelection({ jobId }: { jobId: string }): ReactNode {
  const { client } = usePortal();
  const [response, setResponse] = useState<DesignDecisionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.designDecisions(jobId);
      setResponse(res);
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "The design decisions could not be loaded. Try reloading the screen.",
      );
    } finally {
      setLoading(false);
    }
  }, [client, jobId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rooms = useMemo(() => roomsFrom(response), [response]);
  const existingByRoom = useMemo(() => {
    const map = new Map<string, RoomEmitterInput>();
    for (const d of response?.emitters ?? []) {
      const {
        roomId,
        ...rest
      } = d;
      map.set(roomId, rest);
    }
    return map;
  }, [response]);

  const warningsFor = useCallback(
    (roomId: string) =>
      (response?.computed?.warnings ?? []).filter(
        (w) => w.subjectId === roomId,
      ),
    [response],
  );

  return (
    <>
      <div className="page-header">
        <h1>Emitters — manual selection</h1>
        <p className="lede">
          For each surveyed room, enter the emitter you plan to install
          (radiator or underfloor, retain / replace / new). The calc engine
          (assessEmitters) checks each one at the job's design condition and
          shows per-room warnings inline.
        </p>
        <p>
          <a href={href.job(jobId)}>Back to the job</a>
        </p>
      </div>

      {loading ? (
        <p aria-live="polite">Loading the design decisions…</p>
      ) : null}

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && response && rooms.length === 0 ? (
        <p className="muted">
          No rooms to size against yet. Record a survey on this job first —
          the emitter check needs the room-by-room heat losses to work
          against.
        </p>
      ) : null}

      {rooms.length > 0 ? (
        <section aria-labelledby="emitters-heading" className="stack">
          <h2 id="emitters-heading">Rooms</h2>
          {rooms.map((room) => (
            <RoomEmitterForm
              key={room.id}
              room={room}
              existing={existingByRoom.get(room.id)}
              warnings={warningsFor(room.id)}
              onSave={async (input) => {
                await client.updateRoomEmitter(jobId, room.id, input);
                await load();
              }}
              onDelete={async () => {
                await client.deleteRoomEmitter(jobId, room.id);
                await load();
              }}
            />
          ))}
        </section>
      ) : null}
    </>
  );
}

interface RoomEmitterFormProps {
  readonly room: Room;
  readonly existing: RoomEmitterInput | undefined;
  readonly warnings: readonly { code: string; message: string; severity: string }[];
  readonly onSave: (input: RoomEmitterInput) => Promise<void>;
  readonly onDelete: () => Promise<void>;
}

function RoomEmitterForm({
  room,
  existing,
  warnings,
  onSave,
  onDelete,
}: RoomEmitterFormProps): ReactNode {
  const [kind, setKind] = useState<EmitterKind>(existing?.kind ?? "radiator");
  const [action, setAction] = useState<EmitterAction>(existing?.action ?? "new");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [radiatorKind, setRadiatorKind] = useState<RadiatorKind>(
    existing?.radiatorKind ?? "panel-radiator",
  );
  const [ratedOutputW, setRatedOutputW] = useState(
    existing?.ratedOutputW !== undefined ? String(existing.ratedOutputW) : "",
  );
  const [heatedFloorAreaM2, setHeatedFloorAreaM2] = useState(
    existing?.heatedFloorAreaM2 !== undefined
      ? String(existing.heatedFloorAreaM2)
      : "",
  );
  const [underfloorZone, setUnderfloorZone] = useState<UnderfloorZone>(
    existing?.underfloorZone ?? "occupied",
  );
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const input: RoomEmitterInput =
        kind === "radiator"
          ? {
              kind,
              action,
              description: description.trim() || `${humanise(radiatorKind)} in ${room.name}`,
              radiatorKind,
              ...(ratedOutputW.trim() !== ""
                ? { ratedOutputW: Number(ratedOutputW) }
                : {}),
            }
          : {
              kind,
              action,
              description: description.trim() || `Underfloor heating in ${room.name}`,
              underfloorZone,
              ...(heatedFloorAreaM2.trim() !== ""
                ? { heatedFloorAreaM2: Number(heatedFloorAreaM2) }
                : {}),
            };
      setSaving(true);
      setLocalError(null);
      try {
        await onSave(input);
      } catch (caught) {
        setLocalError(
          caught instanceof ApiError
            ? caught.message
            : "This room's emitter could not be saved. Try again.",
        );
      } finally {
        setSaving(false);
      }
    },
    [
      action,
      description,
      heatedFloorAreaM2,
      kind,
      onSave,
      radiatorKind,
      ratedOutputW,
      room.name,
      underfloorZone,
    ],
  );

  return (
    <form onSubmit={submit} className="stack room-emitter" aria-label={`Emitter for ${room.name}`}>
      <h3>{room.name}</h3>

      <fieldset className="stack">
        <legend>Emitter</legend>

        <label className="field">
          <span>Kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as EmitterKind)}>
            {EMITTER_KINDS.map((k) => (
              <option key={k} value={k}>
                {humanise(k)}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Action</span>
          <select
            value={action}
            onChange={(e) => setAction(e.target.value as EmitterAction)}
          >
            {EMITTER_ACTIONS.map((a) => (
              <option key={a} value={a}>
                {humanise(a)}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Description</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional — defaults to a short description of the choice"
          />
        </label>

        {kind === "radiator" ? (
          <>
            <label className="field">
              <span>Radiator kind</span>
              <select
                value={radiatorKind}
                onChange={(e) => setRadiatorKind(e.target.value as RadiatorKind)}
              >
                {RADIATOR_KINDS.map((r) => (
                  <option key={r} value={r}>
                    {humanise(r)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Rated output (W)</span>
              <input
                type="number"
                step="1"
                value={ratedOutputW}
                onChange={(e) => setRatedOutputW(e.target.value)}
                placeholder="From the manufacturer's datasheet, at their rated ΔT"
              />
            </label>
          </>
        ) : (
          <>
            <label className="field">
              <span>Zone</span>
              <select
                value={underfloorZone}
                onChange={(e) => setUnderfloorZone(e.target.value as UnderfloorZone)}
              >
                {UNDERFLOOR_ZONES.map((z) => (
                  <option key={z} value={z}>
                    {humanise(z)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Heated floor area (m²)</span>
              <input
                type="number"
                step="0.1"
                value={heatedFloorAreaM2}
                onChange={(e) => setHeatedFloorAreaM2(e.target.value)}
              />
            </label>
          </>
        )}
      </fieldset>

      {localError ? (
        <p className="error" role="alert">
          {localError}
        </p>
      ) : null}

      <div className="row">
        <button type="submit" className="primary" disabled={saving}>
          {saving ? "Saving…" : existing ? "Update" : "Save"}
        </button>
        {existing ? (
          <button
            type="button"
            className="link"
            onClick={() => {
              void onDelete();
            }}
          >
            Remove
          </button>
        ) : null}
      </div>

      {warnings.length > 0 ? (
        <ul className="warnings" aria-label={`Warnings for ${room.name}`}>
          {warnings.map((w, i) => (
            <li key={`${w.code}-${i}`}>
              <strong>{w.message}</strong>
              <div className="muted small">
                <code>{w.code}</code> · severity {w.severity}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </form>
  );
}
